/**
 * Razorpay Checkout loader + opener.
 *
 * Every call to `openRazorpay` creates a fresh Razorpay Checkout session,
 * which means every UPI transaction gets its own dynamic QR code — Razorpay
 * regenerates the QR each time you open the modal against a new order_id.
 */
let _scriptPromise = null;

export function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      _scriptPromise = null;
      resolve(false);
    };
    document.body.appendChild(s);
  });
  return _scriptPromise;
}

/**
 * Open the Razorpay Checkout modal.
 *
 * @param {Object} opts
 * @param {string} opts.keyId          Razorpay Key ID (public).
 * @param {string} opts.orderId        Order id from backend.
 * @param {number} opts.amount         Amount in paise.
 * @param {string} opts.currency       "INR".
 * @param {string} opts.name           Merchant name shown in modal.
 * @param {string} opts.description    Short line, e.g. hotel name.
 * @param {Object} opts.prefill        {name, email, contact}.
 * @param {Object} opts.notes          Free-form notes (booking_id, source).
 * @param {string} [opts.method]       Preselect a method: "upi" | "card" | "netbanking" | "wallet" | "emi".
 * @param {(res) => void} opts.onSuccess  Called with {razorpay_payment_id, razorpay_order_id, razorpay_signature}.
 * @param {(err) => void} [opts.onDismiss] Called when user closes without paying.
 */
export async function openRazorpay(opts) {
  const ok = await loadRazorpayScript();
  if (!ok) throw new Error("Payment gateway could not load. Please check your connection.");

  const config = {
    key: opts.keyId,
    order_id: opts.orderId,
    amount: opts.amount,
    currency: opts.currency || "INR",
    name: opts.name || "SwaanStay",
    description: opts.description || "Hotel booking",
    image: opts.image, // optional merchant logo
    prefill: opts.prefill || {},
    notes: opts.notes || {},
    theme: { color: "#0f172a" },
    handler: (response) => {
      try {
        opts.onSuccess?.(response);
      } catch (e) {
        console.error("Razorpay success handler threw", e);
      }
    },
    modal: {
      ondismiss: () => opts.onDismiss?.(),
      escape: true,
      confirm_close: true,
    },
    retry: { enabled: true, max_count: 2 },
  };

  // Preselect payment method (UPI → shows dynamic QR pane by default).
  if (opts.method === "upi") {
    config.method = { upi: true };
    config.config = {
      display: {
        blocks: {
          upi: {
            name: "Pay via UPI",
            instruments: [{ method: "upi", flows: ["qr", "collect", "intent"] }],
          },
        },
        sequence: ["block.upi"],
        preferences: { show_default_blocks: true },
      },
    };
  } else if (opts.method === "card") {
    config.method = { card: true };
  } else if (opts.method === "netbanking") {
    config.method = { netbanking: true };
  }

  const rzp = new window.Razorpay(config);
  rzp.on("payment.failed", (resp) => {
    opts.onDismiss?.(resp?.error);
  });
  rzp.open();
  return rzp;
}
